import { AccordionWizard, Description, Icon, Link, Modal } from '@opengov/component-library/dist';
import { IRecordOf } from '@opengov/compose-app/immutable';
import { BasicColumnTypes } from '@opengov/ogql-types';
import { IConditionalReportColumn } from '@opengov/reporting-types';
import { EConditionalComparisonOperators, IReportColumn } from '@opengov/reporting-types/src/reportColumns-immer';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import React from 'react';

import { IConditionalPreviewTable } from '../../../createConditional/components/ConditionalPreviewTable';
import { customColumnRecords } from '../../../createConditional/conditionalUtils';
import { ICurrentConditionalSetupRecord } from '../../../createConditional/state';
import { EAllowedConditionalTypes, EConditionalCustomValues } from '../../../createConditional/types';
import { IReportLocale } from '../../../locale';

import styles from './CreateConditionalModal.scss';
import { Step1 } from './ModalSteps/ConditionalStep1';
import { Step2 } from './ModalSteps/ConditionalStep2';
import { Step3 } from './ModalSteps/ConditionalStep3';
import { Step4 } from './ModalSteps/ConditionalStep4';

export interface ICreateConditionalModal {
  reportLocaleText: IReportLocale;
  localeFormatter: any;
  ifColumnOptions: any;
  valueToCompareOptions: any;
  optionalSecondValueToCompareOptions: any;
  columns: IReportColumn[];
  conditional: IRecordOf<IConditionalReportColumn<any>>;
  conditionalOptions: any[];
  currentConditionSetup: ICurrentConditionalSetupRecord<any>;
  conditionalPreviewTableProps: IConditionalPreviewTable;
  navigate?: NavigateFunction;
  closeModal(): void;
  goBack(): void;
  addCondition(condition: any): void;
  fetchPreviewData(): void;
  updateColumnType(columnType: BasicColumnTypes): void;
  updateIfValue(column: IReportColumn): void;
  updateCurrentConditionOperator(operator: EAllowedConditionalTypes): void;
  updateCurrentConditionMatchValue(value: any): void;
  updateCurrentConditionLowerBound(value: any): void;
  updateCurrentConditionUpperBound(value: any): void;
  updateCurrentConditionCustomValue(value: any): void;
  updateCurrentConditionReturnedValue(value: any): void;
  updateElseValue(value: any): void;
  updateColumnName(name: string): void;
  saveColumn(column: IConditionalReportColumn<any>, navigate?: NavigateFunction): void;
}

export const previewValueFormatter = (localeFormatter, value, valueType) => {
  switch (valueType) {
    case BasicColumnTypes.Currency:
      return localeFormatter.currency({ value });
    case BasicColumnTypes.Percent:
      return localeFormatter.percent({ value });
    case BasicColumnTypes.Numeric:
      return localeFormatter.number({ value });
    default:
      return value;
  }
};

export function getOperatorLocaleText(conditionalModalLocale: any, operator: string): string {
  const {
    greaterThanOperator,
    greaterThanOrEqualToOperator,
    lessThanOperator,
    lessThanOrEqualToOperator,
    equalToOperator,
    notEqualToOperator,
    betweenOperator
  } = conditionalModalLocale;
  switch (operator) {
    case 'greaterThan':
      return greaterThanOperator;
    case 'greaterThanOrEqualTo':
      return greaterThanOrEqualToOperator;
    case 'lessThan':
      return lessThanOperator;
    case 'lessThanOrEqualTo':
      return lessThanOrEqualToOperator;
    case 'equalTo':
      return equalToOperator;
    case 'notEqualTo':
      return notEqualToOperator;
    case 'between':
      return betweenOperator;
    default:
      return equalToOperator;
  }
}

export function getColumnTypeText(conditionalModalLocale: any, columnType: string): string {
  const { textColumn, numberColumn, currencyColumn, percentageColumn, dateColumn } = conditionalModalLocale;
  switch (columnType) {
    case BasicColumnTypes.Text:
      return textColumn;
    case BasicColumnTypes.Numeric:
      return numberColumn;
    case BasicColumnTypes.Currency:
      return currencyColumn;
    case BasicColumnTypes.Percent:
      return percentageColumn;
    case BasicColumnTypes.Date:
      return dateColumn;
    default:
      return textColumn;
  }
}

export function isDateValid(date: string): boolean {
  const unformattedDate = date.replace(/ \/ | \/| /g, '').replace(/\D/g, '');
  const numericDateValues = {
    month: Number(unformattedDate.substr(0, 2)) || null,
    day: Number(unformattedDate.substr(2, 2)) || null,
    year: Number(unformattedDate.substr(4, 4)) || null
  };
  const dateTime = DateTime.fromObject(numericDateValues);
  const isCompleteDate = unformattedDate.length === 8;
  return dateTime.isValid && isCompleteDate;
}

export function getCustomValuesComplete(currentConditionSetup: any, conditional: any): boolean {
  const { matchValue, lowerBound, upperBound } = currentConditionSetup;
  const sourceColumnIsDate = conditional.sourceColumn.type === BasicColumnTypes.Date;
  const betweenIsCurrentOperator =
    currentConditionSetup && currentConditionSetup.operator === EConditionalComparisonOperators.between;
  let customValuesComplete = true;
  if (
    !betweenIsCurrentOperator &&
    matchValue &&
    matchValue.columnType === 'constant' &&
    ((matchValue.value !== null && matchValue.value.trim() === '') ||
      matchValue.value === null ||
      (sourceColumnIsDate && !isDateValid(matchValue.value)))
  ) {
    customValuesComplete = false;
  }
  if (
    betweenIsCurrentOperator &&
    lowerBound &&
    lowerBound.columnType === 'constant' &&
    ((lowerBound.value !== null && lowerBound.value.trim() === '') ||
      lowerBound.value === null ||
      (sourceColumnIsDate && !isDateValid(lowerBound.value)))
  ) {
    customValuesComplete = false;
  }
  if (
    betweenIsCurrentOperator &&
    upperBound &&
    upperBound.columnType === 'constant' &&
    ((upperBound.value !== null && upperBound.value.trim() === '') ||
      upperBound.value === null ||
      (sourceColumnIsDate && !isDateValid(upperBound.value)))
  ) {
    customValuesComplete = false;
  }
  return customValuesComplete;
}

export function handleValueSelect(columns: any, value: string, updaterFunction: any) {
  if (
    value === EConditionalCustomValues.Currency ||
    value === EConditionalCustomValues.Numeric ||
    value === EConditionalCustomValues.Percentage ||
    value === EConditionalCustomValues.Year ||
    value === EConditionalCustomValues.Date
  ) {
    updaterFunction(customColumnRecords[value]);
  } else if (typeof value === 'string') {
    updaterFunction(columns.find((c) => c.name === value));
  } else {
    updaterFunction(value);
  }
}

export class CreateConditionalModal extends React.Component<ICreateConditionalModal> {
  getOperatorLocaleText = (operator: string) =>
    getOperatorLocaleText(this.props.reportLocaleText.calculations.conditionalModal, operator);

  getColumnTypeText = (columnType: string) =>
    getColumnTypeText(this.props.reportLocaleText.calculations.conditionalModal, columnType);

  getStepSummary = (step) => {
    const {
      conditional,
      currentConditionSetup,
      reportLocaleText: {
        calculations: {
          conditionalModal: { wordIf, wordAnd, wordThen, wordElse }
        }
      }
    } = this.props;

    const customValueOrColumnName = (valueName) => {
      return currentConditionSetup[valueName] && currentConditionSetup[valueName].columnType === 'constant'
        ? previewValueFormatter(
            this.props.localeFormatter,
            currentConditionSetup[valueName] && currentConditionSetup[valueName].value,
            conditional.sourceColumn.type
          )
        : currentConditionSetup[valueName] && currentConditionSetup[valueName].displayName;
    };

    switch (step) {
      case '1':
        return <Description dataTest="step-1-summary">{this.getColumnTypeText(conditional.type)}</Description>;
      case '2':
        return (
          <Description dataTest="step-2-summary">
            <b>{wordIf}</b>&nbsp;{conditional.sourceColumn.displayName}
          </Description>
        );
      case '3':
        return (
          <Description dataTest="step-3-summary">
            {this.getOperatorLocaleText(currentConditionSetup.operator)}
            &nbsp;{customValueOrColumnName('matchValue')}
            &nbsp;<b>{wordThen}</b>
            &nbsp;
            {previewValueFormatter(this.props.localeFormatter, currentConditionSetup.returnedValue, conditional.type)}
            &nbsp;<b>{wordElse}</b>
            &nbsp;{previewValueFormatter(this.props.localeFormatter, conditional.else, conditional.type)}
          </Description>
        );
      case '3-between':
        return (
          <Description dataTest="step-3-between-summary">
            {this.getOperatorLocaleText(currentConditionSetup.operator)}
            &nbsp;{customValueOrColumnName('lowerBound')}
            &nbsp;{wordAnd}
            &nbsp;{customValueOrColumnName('upperBound')}
            &nbsp;<b>{wordThen}</b>
            &nbsp;
            {previewValueFormatter(this.props.localeFormatter, currentConditionSetup.returnedValue, conditional.type)}
            &nbsp;<b>{wordElse}</b>
            &nbsp;{previewValueFormatter(this.props.localeFormatter, conditional.else, conditional.type)}
          </Description>
        );
      default:
        break;
    }
  };

  getStepComplete = (step) => {
    const { conditional, currentConditionSetup } = this.props;
    const betweenIsCurrentOperator =
      currentConditionSetup && currentConditionSetup.operator === EConditionalComparisonOperators.between;
    const step3FieldsComplete =
      currentConditionSetup.operator !== null &&
      conditional.else !== null &&
      (typeof conditional.else === 'string' ? conditional.else.trim() !== '' : true) &&
      currentConditionSetup.returnedValue !== null &&
      (conditional.type === BasicColumnTypes.Date ? this.isDateValid(conditional.else) : true) &&
      (typeof currentConditionSetup.returnedValue === 'string'
        ? currentConditionSetup.returnedValue.trim() !== ''
        : true) &&
      (conditional.type === BasicColumnTypes.Date ? this.isDateValid(currentConditionSetup.returnedValue) : true) &&
      (betweenIsCurrentOperator
        ? currentConditionSetup.lowerBound !== null && currentConditionSetup.upperBound !== null
        : currentConditionSetup.matchValue !== null);
    switch (step) {
      case 2:
        return conditional.sourceColumn.name !== '';
      case 3:
        return step3FieldsComplete && this.getCustomValuesComplete();
      case 4:
        return (
          conditional.displayName.trim() !== '' &&
          conditional.displayName !== null &&
          conditional.displayName.length <= 30
        );
      default:
        return false;
    }
  };

  isDateValid = (date: string) => isDateValid(date);

  getCustomValuesComplete = () => getCustomValuesComplete(this.props.currentConditionSetup, this.props.conditional);

  handleValueSelect = (value: string, updaterFunction: any) => {
    handleValueSelect(this.props.columns, value, updaterFunction);
  };

  submit = () => {
    this.props.saveColumn(this.props.conditional, this.props.navigate);
  };

  render() {
    const {
      ifColumnOptions,
      valueToCompareOptions,
      optionalSecondValueToCompareOptions,
      conditional,
      conditionalOptions,
      currentConditionSetup,
      conditionalPreviewTableProps,
      reportLocaleText: {
        calculations: {
          conditionalModal: {
            createNewColumn,
            backButtonText,
            nextButtonText,
            submitButtonText,
            step1Title,
            step2Title,
            step3ATitle,
            step4Title,
            returnToStart
          }
        }
      },
      closeModal,
      goBack,
      updateColumnType,
      updateIfValue,
      updateCurrentConditionOperator,
      updateCurrentConditionMatchValue,
      updateCurrentConditionLowerBound,
      updateCurrentConditionUpperBound,
      updateCurrentConditionReturnedValue,
      updateCurrentConditionCustomValue,
      updateElseValue,
      updateColumnName
    } = this.props;

    const betweenIsCurrentOperator =
      currentConditionSetup && currentConditionSetup.operator === EConditionalComparisonOperators.between;

    return (
      <Modal.Work.Wide title={createNewColumn} onClose={closeModal}>
        <AccordionWizard
          dataTest="create-conditional-column-wizard"
          backButtonText={backButtonText}
          nextButtonText={nextButtonText}
          submitButtonText={submitButtonText}
          activeStepIndex={0}
          onSubmit={this.submit}
        >
          <AccordionWizard.Step
            label={step1Title}
            index={0}
            keyLabel="1"
            isComplete={this.getStepComplete(1)}
            buttonDisabled={false}
            summary={this.getStepSummary('1')}
          >
            <Step1
              conditional={conditional}
              reportLocaleText={this.props.reportLocaleText}
              updateColumnType={updateColumnType}
            />
          </AccordionWizard.Step>
          <AccordionWizard.Step
            label={step2Title}
            index={1}
            keyLabel="2"
            isComplete={this.getStepComplete(2)}
            buttonDisabled={!this.getStepComplete(2)}
            summary={this.getStepComplete(2) ? this.getStepSummary('2') : null}
          >
            <Step2
              reportLocaleText={this.props.reportLocaleText}
              ifColumnOptions={ifColumnOptions}
              columns={this.props.columns}
              conditional={conditional}
              handleValueSelect={this.handleValueSelect}
              updateIfValue={updateIfValue}
            />
          </AccordionWizard.Step>
          <AccordionWizard.Step
            label={step3ATitle}
            index={2}
            keyLabel="3"
            isComplete={this.getStepComplete(3)}
            summary={
              betweenIsCurrentOperator
                ? this.getStepComplete(3)
                  ? this.getStepSummary('3-between')
                  : null
                : this.getStepComplete(3)
                  ? this.getStepSummary('3')
                  : null
            }
            buttonDisabled={!this.getStepComplete(3)}
          >
            <Step3
              reportLocaleText={this.props.reportLocaleText}
              valueToCompareOptions={valueToCompareOptions}
              optionalSecondValueToCompareOptions={optionalSecondValueToCompareOptions}
              columns={this.props.columns}
              conditional={conditional}
              conditionalOptions={conditionalOptions}
              currentConditionSetup={currentConditionSetup}
              handleValueSelect={this.handleValueSelect}
              updateCurrentConditionOperator={updateCurrentConditionOperator}
              updateCurrentConditionMatchValue={updateCurrentConditionMatchValue}
              updateCurrentConditionLowerBound={updateCurrentConditionLowerBound}
              updateCurrentConditionUpperBound={updateCurrentConditionUpperBound}
              updateCurrentConditionReturnedValue={updateCurrentConditionReturnedValue}
              updateCurrentConditionCustomValue={updateCurrentConditionCustomValue}
              updateElseValue={updateElseValue}
            />
          </AccordionWizard.Step>
          <AccordionWizard.Step
            label={step4Title}
            index={3}
            keyLabel="4"
            isComplete={this.getStepComplete(4)}
            buttonDisabled={!this.getStepComplete(4)}
          >
            <Step4
              reportLocaleText={this.props.reportLocaleText}
              conditional={conditional}
              currentConditionSetup={currentConditionSetup}
              conditionalPreviewTableProps={conditionalPreviewTableProps}
              updateColumnName={updateColumnName}
              fetchPreviewData={this.props.fetchPreviewData}
              addCondition={this.props.addCondition}
            />
          </AccordionWizard.Step>
        </AccordionWizard>
        <div className={styles.twoUnitSpacer} />
        <div className={styles.returnToStart}>
          <Icon icon="arrow-left-small" />
          <Link.Quiet href="#" text={returnToStart} onClick={goBack} dataTest="return-to-start-button" />
        </div>
      </Modal.Work.Wide>
    );
  }
}

export function EnhancedCreateConditionalModal(props: ICreateConditionalModal) {
  const navigate = useNavigate();
  return <CreateConditionalModal {...props} navigate={navigate} />;
}
